// src/components/ecommerce/EcommerceSettings.jsx
import React, { useState, useEffect } from 'react';
import { FileUpload } from '../common/FileUpload';
import { Link, ExternalLink } from 'lucide-react';
import { 
  X, 
  Save, 
  Upload, 
  Image as ImageIcon, 
  Eye, 
  Store,
  Building2,
  CreditCard,
  Truck,
  Receipt,
  Users,
  BarChart3,
  Palette,
  Globe,
  Lock, 
  Plus,
  Settings,
  Trash2,
  Loader2,
  AlertCircle,
  Check,
  Play,
  Monitor,
  Smartphone,
  CheckCircle,
  Tablet,
  ShoppingBag,
  Package,
  Mail,
  Phone,
  MapPin,
  Clock,
  Shield,
  Zap,
  Percent,
  Tag,
  Star,
  Heart,
  MessageCircle,
  Share2,
  Search,
  Filter,
  Grid,
  List,
  Layout,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Linkedin
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { firestoreService } from '../../services/firestoreService';
import { DigitalMallTemplate } from './DigitalMallTemplate';

import { ViewSettings } from '../settings/shared/ViewSettings';
import { PermissionsSettings } from '../settings/shared/PermissionsSettings'; 
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { LayoutDisplaySection } from '../settings/shared/LayoutSection';
import { PaymentsSection } from '../settings/shared/PaymentsSection';
import ShippingSection from '../settings/shared/ShippingSection';

const STORE_TYPES = {
  'general': {
    label: 'General Store',
    icon: Store,
    description: 'Multi-category online store for various products',
    defaultFeatures: ['cart', 'wishlist', 'reviews', 'inventory', 'variants'],
    color: '#3b82f6'
  },
  'restaurant': {
    label: 'Restaurant/Food',
    icon: Package,
    description: 'Food delivery and restaurant ordering system',
    defaultFeatures: ['cart', 'inventory', 'bookings'],
    color: '#f59e0b'
  },
  'services': {
    label: 'Service Business',
    icon: Users,
    description: 'Service booking and appointment scheduling',
    defaultFeatures: ['bookings', 'reviews'],
    color: '#10b981'
  },
  'digital': {
    label: 'Digital Products',
    icon: Zap,
    description: 'Software, courses, digital downloads',
    defaultFeatures: ['cart', 'digitalProducts', 'subscriptions'],
    color: '#8b5cf6'
  },
  'rental': {
    label: 'Rental/Booking',
    icon: Clock,
    description: 'Equipment rental and property booking',
    defaultFeatures: ['bookings', 'inventory'],
    color: '#ef4444'
  },
  'marketplace': {
    label: 'Marketplace',
    icon: Globe,
    description: 'Multi-vendor marketplace platform',
    defaultFeatures: ['cart', 'wishlist', 'reviews', 'multiVendor'],
    color: '#06b6d4'
  },
  'mall': {
    label: 'Digital Mall',
    icon: Store,
    description: 'Host multiple shops and services with centralized checkout options',
    defaultFeatures: ['cart', 'multiVendor', 'bookings'],
    color: '#4f46e5'
  }
};

const AVAILABLE_FEATURES = {
  cart: { label: 'Shopping Cart', icon: ShoppingBag, description: 'Add to cart and checkout functionality' },
  wishlist: { label: 'Wishlist', icon: Heart, description: 'Save favorite products for later' },
  reviews: { label: 'Reviews & Ratings', icon: Star, description: 'Customer reviews and product ratings' },
  inventory: { label: 'Inventory Tracking', icon: Package, description: 'Stock management and tracking' },
  variants: { label: 'Product Variants', icon: Settings, description: 'Color, size, and other product options' },
  digitalProducts: { label: 'Digital Products', icon: Zap, description: 'Downloadable and digital items' },
  subscriptions: { label: 'Subscriptions', icon: Receipt, description: 'Recurring payment products' },
  bookings: { label: 'Bookings/Appointments', icon: Clock, description: 'Service booking and scheduling' },
  multiVendor: { label: 'Multi-Vendor', icon: Users, description: 'Multiple sellers on one platform' },
  giftCards: { label: 'Gift Cards', icon: Tag, description: 'Digital gift card system' },
  loyaltyProgram: { label: 'Loyalty Program', icon: Star, description: 'Customer loyalty and rewards' },
  affiliateProgram: { label: 'Affiliate Program', icon: Share2, description: 'Affiliate marketing system' }
};

const DESIGN_THEMES = {
  modern: { label: 'Modern', preview: '🎨', description: 'Clean, contemporary design' },
  classic: { label: 'Classic', preview: '📰', description: 'Traditional, professional layout' },
  minimal: { label: 'Minimal', preview: '⚪', description: 'Simple, clean aesthetic' },
  creative: { label: 'Creative', preview: '🌈', description: 'Bold, artistic design' }
};

export const EcommerceSettings = ({ book, bookId, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [previewMode, setPreviewMode] = useState('desktop');

  const [uploadStates, setUploadStates] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const [newUserEmail, setNewUserEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [newMallStore, setNewMallStore] = useState({
    storeId: '',
    storeName: '',
    description: ''
  });
  const [formData, setFormData] = useState({
    // Store Type & Basic Info
    storeType: book?.storeType || 'general',
    botName: book?.botName || book?.title || '',
    tagline: book?.tagline || '',
    description: book?.description || '',
    
    // MOVED TO ROOT LEVEL for serveBook function
    customDomain: book?.customDomain || '',
    customPath: book?.customPath || '',
    
    // Contact Info
    email: book?.email || '',
    phone: book?.phone || '',
    address: book?.address || '',
    website: book?.website || '',
    
    // Business Hours
    businessHours: book?.businessHours || {
      monday: '9:00 AM - 6:00 PM',
      tuesday: '9:00 AM - 6:00 PM',
      wednesday: '9:00 AM - 6:00 PM',
      thursday: '9:00 AM - 6:00 PM',
      friday: '9:00 AM - 6:00 PM',
      saturday: '9:00 AM - 4:00 PM',
      sunday: 'Closed'
    },
    businessType: book?.businessType || 'retail',
    
    // Files
    logoUrl: book?.logoUrl || '',
    logoHeight: 40,
    favicon: book?.favicon || '',
    headerBackgroundUrl: book?.headerBackgroundUrl || '',
    ogImage: book?.ogImage || '',
    
    // Publishing
    published: book?.published !== false,
    schedulePublication: book?.schedulePublication || false,
    publicationDate: book?.publicationDate || '',
    publicationTime: book?.publicationTime || '',
    
    // Access & Privacy Settings
    accessLevel: book?.accessLevel || 'public',
    requireLogin: book?.requireLogin || false,
    allowIndexing: book?.allowIndexing !== false,
    authorizedUsers: book?.authorizedUsers || [],
    allowPrint: book?.allowPrint !== false,
    allowDownload: book?.allowDownload || false,
    enableRightClickProtection: book?.enableRightClickProtection || false,
    showLastUpdate: book?.showLastUpdate !== false,
    requireAge: book?.requireAge || false,
    ageLimit: book?.ageLimit || 18,
    requireTerms: book?.requireTerms || false,
    passwordProtected: book?.passwordProtected || false,
    accessPassword: book?.accessPassword || '',
    allowGuestCheckout: book?.allowGuestCheckout !== false,
    requireAccountForPurchase: book?.requireAccountForPurchase || false,
    
    // Store Features
    enabledFeatures: {
      cart: true,
      wishlist: true,
      reviews: true,
      inventory: true,
      variants: true,
      digitalProducts: false,
      subscriptions: false,
      bookings: false,
      multiVendor: false,
      giftCards: false,
      loyaltyProgram: false,
      affiliateProgram: false,
      ...book?.enabledFeatures
    },
    
    // Design Settings - COMPLETE with missing properties
    design: {
      theme: book?.design?.theme || 'modern',
      layout: book?.design?.layout || 'grid',
      productsPerPage: book?.design?.productsPerPage || 12,
      gridColumns: book?.design?.gridColumns || 3, // ADD MISSING
      showHero: book?.design?.showHero !== false,
      showFeatured: book?.design?.showFeatured !== false,
      showCategories: book?.design?.showCategories !== false,
      showPromos: book?.design?.showPromos !== false,
      primaryColor: book?.design?.primaryColor || book?.customStyling?.primaryColor || '#3b82f6',
      accentColor: book?.design?.accentColor || '#f59e0b',
      backgroundColor: book?.design?.backgroundColor || book?.customStyling?.backgroundColor || '#ffffff',
      headerBackgroundColor: book?.design?.headerBackgroundColor || book?.customStyling?.headerBackgroundColor || '#ffffff',
      headerTextColor: book?.design?.headerTextColor || '#1f2937',
      fontFamily: book?.design?.fontFamily || 'Inter',
      cardRadius: book?.design?.cardRadius || '12px',
      shadowLevel: book?.design?.shadowLevel || 'medium',
      imageFit: book?.design?.imageFit || 'cover', // ADD MISSING
      imageAspectRatio: book?.design?.imageAspectRatio || '1:1' // ADD MISSING
    },
    
    // SEO & Marketing (customDomain/customPath MOVED to root)
    seoTitle: book?.seoTitle || '',
    seoDescription: book?.seoDescription || '',
    seoKeywords: book?.seoKeywords || '',
    
    // Social Media
    socialMedia: {
      facebook: book?.socialMedia?.facebook || '',
      instagram: book?.socialMedia?.instagram || '',
      twitter: book?.socialMedia?.twitter || '',
      tiktok: book?.socialMedia?.tiktok || '',
      youtube: book?.socialMedia?.youtube || '',
      linkedin: book?.socialMedia?.linkedin || ''
    },
    
    // Integrations
    integrations: {
      whatsapp: book?.integrations?.whatsapp || '',
      googleAnalytics: book?.integrations?.googleAnalytics || '',
      facebookPixel: book?.integrations?.facebookPixel || '',
      newsletter: book?.integrations?.newsletter || ''
    },
    
    // Enhanced Business Info
    businessName: book?.businessName || '',
    businessRegistration: book?.businessRegistration || '',
    vatNumber: book?.vatNumber || '',
    whatsappNumber: book?.whatsappNumber || '',
    customerServiceEmail: book?.customerServiceEmail || '',
    city: book?.city || '',
    province: book?.province || '',
    postalCode: book?.postalCode || '',
    
    // Enhanced Payment Methods
    paymentMethods: {
      payfast: {
        enabled: false,
        merchantId: '',
        merchantKey: '',
        sandbox: true
      },
      eft: {
        enabled: false,
        bankName: '',
        accountHolder: '',
        accountNumber: '',
        branchCode: '',
        instructions: ''
      },
      cod: {
        enabled: false,
        acceptCash: true,
        acceptCard: true,
        fee: 0
      },
      ...book?.paymentMethods
    },
    
    // Enhanced Payment Settings
    paymentSettings: {
      requireTerms: false,
      emailConfirmations: true,
      savePaymentMethods: false,
      ...book?.paymentSettings
    },
    
    // Enhanced Shipping
    shipping: {
      enabled: true,
      deliveryZones: [
        {
          name: 'Local Area',
          fee: 50,
          deliveryTime: '1-2 business days',
          areas: 'Durban, Umhlanga'
        }
      ],
      freeShippingEnabled: false,
      freeShippingThreshold: 500,
      freeShippingMessage: 'Free shipping on orders over R500!',
      allowPickup: true,
      handlingFee: 0,
      requireSignature: false,
      processingTime: '1-2 business days',
      ...book?.shipping
    },
    
    tax: {
      enabled: book?.tax?.enabled || false,
      rate: book?.tax?.rate || 0,
      inclusive: book?.tax?.inclusive || false,
      ...book?.tax
    },
    
    // Inventory
    inventory: {
      trackStock: book?.inventory?.trackStock !== false,
      lowStockThreshold: book?.inventory?.lowStockThreshold || 10,
      allowBackorders: book?.inventory?.allowBackorders || false,
      outOfStockVisibility: book?.inventory?.outOfStockVisibility || 'show',
      ...book?.inventory
    },
    
    // Customer Settings
    customer: {
      accountRequired: book?.customer?.accountRequired || false,
      guestCheckout: book?.customer?.guestCheckout !== false,
      emailVerification: book?.customer?.emailVerification || false,
      ...book?.customer
    },
    
    // Advanced Settings
    advanced: {
      customCSS: book?.advanced?.customCSS || '',
      maintenance: book?.advanced?.maintenance || false,
      passwordProtection: book?.advanced?.passwordProtection || false,
      password: book?.advanced?.password || '',
      ...book?.advanced
    },

    mallSettings: {
      mallName: book?.mallSettings?.mallName || book?.botName || book?.title || '',
      tagline: book?.mallSettings?.tagline || '',
      centralizedCheckout: book?.mallSettings?.centralizedCheckout !== false,
      checkoutUrl: book?.mallSettings?.checkoutUrl || '',
      whatsapp: book?.mallSettings?.whatsapp || '',
      email: book?.mallSettings?.email || '',
      stores: Array.isArray(book?.mallSettings?.stores) ? book.mallSettings.stores : []
    },
  });

  useEffect(() => {
    if (book && isOpen) {
      initializeFormData();
    }
  }, [book, isOpen]);

  const initializeFormData = () => {
    // Get store type config
    const storeType = book?.storeType || 'general';
    const storeConfig = STORE_TYPES[storeType];
    
    setFormData({
      // Store Type & Basic Info - read from book
      storeType,
      botName: book?.botName || book?.title || '',
      tagline: book?.tagline || '',
      description: book?.description || '',
      
      // Domain settings - read from book
      customDomain: book?.customDomain || '',
      customPath: book?.customPath || '',
      
      // Contact Info - read from book
      email: book?.email || '',
      phone: book?.phone || '',
      address: book?.address || '',
      website: book?.website || '',
      
      // Business Hours - read from book or use defaults
      businessHours: book?.businessHours || {
        monday: '9:00 AM - 6:00 PM',
        tuesday: '9:00 AM - 6:00 PM',
        wednesday: '9:00 AM - 6:00 PM',
        thursday: '9:00 AM - 6:00 PM',
        friday: '9:00 AM - 6:00 PM',
        saturday: '9:00 AM - 4:00 PM',
        sunday: 'Closed'
      },
      businessType: book?.businessType || 'retail',
      
      // Files - read from book
      logoUrl: book?.logoUrl || '',
      logoHeight: book?.logoHeight || 40,
      faviconUrl: book?.faviconUrl || '',
      headerBackgroundUrl: book?.headerBackgroundUrl || '',
      ogImage: book?.ogImage || '',
      
      // Publishing - read from book
      published: book?.published ?? true,
      schedulePublication: book?.schedulePublication || false,
      publicationDate: book?.publicationDate || '',
      publicationTime: book?.publicationTime || '',
      
      // Access & Privacy - read from book
      accessLevel: book?.accessLevel || 'public',
      requireLogin: book?.requireLogin || false,
      allowIndexing: book?.allowIndexing ?? true,
      authorizedUsers: book?.authorizedUsers || [],
      allowPrint: book?.allowPrint ?? true,
      allowDownload: book?.allowDownload || false,
      enableRightClickProtection: book?.enableRightClickProtection || false,
      showLastUpdate: book?.showLastUpdate ?? true,
      requireAge: book?.requireAge || false,
      ageLimit: book?.ageLimit || 18,
      requireTerms: book?.requireTerms || false,
      passwordProtected: book?.passwordProtected || false,
      password: book?.password || '',
      
      // SEO - read from book
      seoTitle: book?.seoTitle || '',
      seoDescription: book?.seoDescription || '',
      seoKeywords: book?.seoKeywords || [],
      enableStructuredData: book?.enableStructuredData ?? true,
      socialLinks: book?.socialLinks || {
        facebook: '',
        instagram: '',
        twitter: '',
        linkedin: '',
        youtube: ''
      },
      
      // Header Settings - read from book with proper fallbacks
      headerSettings: {
        showLogo: book?.headerSettings?.showLogo ?? true,
        showTitle: book?.headerSettings?.showTitle ?? true,
        showDescription: book?.headerSettings?.showDescription ?? false,
        showSearch: book?.headerSettings?.showSearch ?? true,
        showShareButton: book?.headerSettings?.showShareButton ?? true,
        showAccessLevel: book?.headerSettings?.showAccessLevel ?? true,
        showFilterControls: book?.headerSettings?.showFilterControls ?? true,
        showGallerySelector: book?.headerSettings?.showGallerySelector ?? true,
        showViewModeToggle: book?.headerSettings?.showViewModeToggle ?? true,
        showOwnerControls: book?.headerSettings?.showOwnerControls ?? true,
        headerBackgroundColor: book?.headerSettings?.headerBackgroundColor || '#ffffff',
        headerTextColor: book?.headerSettings?.headerTextColor || '#000000',
        headerHeight: book?.headerSettings?.headerHeight || 'normal',
        logoHeight: book?.headerSettings?.logoHeight || 40,
        logoPosition: book?.headerSettings?.logoPosition || 'left',
        stickyHeader: book?.headerSettings?.stickyHeader ?? true,
        showBreadcrumbs: book?.headerSettings?.showBreadcrumbs ?? true,
        headerStyle: book?.headerSettings?.headerStyle || 'default',
        headerShadow: book?.headerSettings?.headerShadow ?? true,
        // Spread any additional settings
        ...book?.headerSettings
      },
      
      // Layout Settings - read from book with proper fallbacks
      layoutSettings: {
        gridColumns: book?.layoutSettings?.gridColumns || 3,
        gridGap: book?.layoutSettings?.gridGap || 'normal',
        cardStyle: book?.layoutSettings?.cardStyle || 'elevated',
        showCardShadows: book?.layoutSettings?.showCardShadows ?? true,
        cardBorderRadius: book?.layoutSettings?.cardBorderRadius || 'medium',
        enableSectionAnchors: book?.layoutSettings?.enableSectionAnchors ?? true,
        showSectionDividers: book?.layoutSettings?.showSectionDividers ?? true,
        enableSmoothScroll: book?.layoutSettings?.enableSmoothScroll ?? true,
        showTableOfContents: book?.layoutSettings?.showTableOfContents ?? false,
        stickyNavigation: book?.layoutSettings?.stickyNavigation ?? true,
        containerWidth: book?.layoutSettings?.containerWidth || 'normal',
        sectionSpacing: book?.layoutSettings?.sectionSpacing || 'normal',
        animationStyle: book?.layoutSettings?.animationStyle || 'fade',
        // Spread any additional settings
        ...book?.layoutSettings
      },

      paymentSettings: {
        // Payment Processing
        paymentProcessor: book?.paymentSettings?.paymentProcessor || 'stripe',
        currency: book?.paymentSettings?.currency || 'USD',
        
        // Stripe Configuration
        stripePublishableKey: book?.paymentSettings?.stripePublishableKey || '',
        stripeSecretKey: book?.paymentSettings?.stripeSecretKey || '',
        
        // PayPal Configuration
        paypalClientId: book?.paymentSettings?.paypalClientId || '',
        paypalClientSecret: book?.paymentSettings?.paypalClientSecret || '',
        
        // Square Configuration
        squareApplicationId: book?.paymentSettings?.squareApplicationId || '',
        squareAccessToken: book?.paymentSettings?.squareAccessToken || '',
        squareLocationId: book?.paymentSettings?.squareLocationId || '',
        
        // PayFast Configuration (South Africa)
        payfastMerchantId: book?.paymentSettings?.payfastMerchantId || '',
        payfastMerchantKey: book?.paymentSettings?.payfastMerchantKey || '',
        payfastPassphrase: book?.paymentSettings?.payfastPassphrase || '',
        payfastSandbox: book?.paymentSettings?.payfastSandbox !== false,
        
        // Pricing & Fees
        processingFeePercent: book?.paymentSettings?.processingFeePercent || 2.9,
        fixedFee: book?.paymentSettings?.fixedFee || 0.30,
        minimumAmount: book?.paymentSettings?.minimumAmount || 1.00,
        
        // Tax Configuration
        taxType: book?.paymentSettings?.taxType || 'none',
        taxRate: book?.paymentSettings?.taxRate || 0,
        taxLabel: book?.paymentSettings?.taxLabel || 'Tax',
        taxNumber: book?.paymentSettings?.taxNumber || '',
        
        // Payment Options
        acceptCreditCards: book?.paymentSettings?.acceptCreditCards !== false,
        acceptDebitCards: book?.paymentSettings?.acceptDebitCards !== false,
        acceptApplePay: book?.paymentSettings?.acceptApplePay || false,
        acceptGooglePay: book?.paymentSettings?.acceptGooglePay || false,
        acceptPayPal: book?.paymentSettings?.acceptPayPal || false,
        acceptBankTransfer: book?.paymentSettings?.acceptBankTransfer || false,
        acceptCrypto: book?.paymentSettings?.acceptCrypto || false,
        
        // Security & Compliance
        require3DSecure: book?.paymentSettings?.require3DSecure || false,
        enableFraudDetection: book?.paymentSettings?.enableFraudDetection !== false,
        pciCompliant: book?.paymentSettings?.pciCompliant !== false,
        sslEnabled: book?.paymentSettings?.sslEnabled !== false,
        
        // Refunds & Disputes
        allowRefunds: book?.paymentSettings?.allowRefunds !== false,
        refundPeriodDays: book?.paymentSettings?.refundPeriodDays || 30,
        autoRefundFailed: book?.paymentSettings?.autoRefundFailed !== false,
        disputeResolution: book?.paymentSettings?.disputeResolution || 'manual',
        
        // Invoicing
        enableInvoicing: book?.paymentSettings?.enableInvoicing || false,
        invoicePrefix: book?.paymentSettings?.invoicePrefix || 'INV',
        invoiceNumberFormat: book?.paymentSettings?.invoiceNumberFormat || 'sequential',
        sendInvoiceEmails: book?.paymentSettings?.sendInvoiceEmails !== false,
        invoiceFooterText: book?.paymentSettings?.invoiceFooterText || '',
        
        // Payment Notifications
        notifyOnPayment: book?.paymentSettings?.notifyOnPayment !== false,
        notifyOnRefund: book?.paymentSettings?.notifyOnRefund !== false,
        notifyOnDispute: book?.paymentSettings?.notifyOnDispute !== false,
        paymentNotificationEmail: book?.paymentSettings?.paymentNotificationEmail || '',
        
        // Additional Payment Settings
        enableSaveCards: book?.paymentSettings?.enableSaveCards !== false,
        enableAutoCharge: book?.paymentSettings?.enableAutoCharge || false,
        enableSubscriptions: book?.paymentSettings?.enableSubscriptions || false,
        enablePaymentPlans: book?.paymentSettings?.enablePaymentPlans || false,
      },
      
      // Design Settings - read from book
      design: book?.design || {
        primaryColor: storeConfig?.color || '#3b82f6',
        secondaryColor: '#64748b',
        accentColor: '#f59e0b',
        backgroundColor: '#ffffff',
        textColor: '#1f2937',
        fontFamily: 'Inter, sans-serif',
        theme: 'modern'
      },
      
      // View Settings - read from book
      viewSettings: book?.viewSettings || {
        enablePublicView: true,
        enableManagementView: true,
        publicViewLabel: 'Store',
        managementViewLabel: 'Admin Panel',
        defaultView: 'public',
        requireAuth: false,
        allowedRoles: ['owner', 'admin', 'manager']
      },
      
      // Permissions - read from book
      permissions: book?.permissions || {},
      
      // Enabled Features - read from book or use store defaults
      enabledFeatures: book?.enabledFeatures || storeConfig?.defaultFeatures?.reduce((acc, feature) => {
        acc[feature] = true;
        return acc;
      }, {}) || {},
      
      // Shipping - read from book
      shipping: book?.shipping || {
        enabled: true,
        freeShippingThreshold: 0,
        defaultShippingCost: 0,
        allowPickup: true,
        requireSignature: false,
        processingTime: '1-2 business days',
        maxDeliveryTime: '7-10 business days'
      },
      
      // Tax - read from book
      tax: book?.tax || {
        enabled: false,
        rate: 0,
        inclusive: false
      },
      
      // Inventory - read from book
      inventory: book?.inventory || {
        trackStock: true,
        lowStockThreshold: 10,
        allowBackorders: false,
        outOfStockVisibility: 'show'
      },
      
      // Customer Settings - read from book
      customer: book?.customer || {
        accountRequired: false,
        guestCheckout: true,
        emailVerification: false
      },
      
      // Advanced Settings - read from book
      advanced: book?.advanced || {
        customCSS: '',
        maintenance: false,
        passwordProtection: false,
        password: ''
      },

      mallSettings: {
        mallName: book?.mallSettings?.mallName || book?.botName || book?.title || '',
        tagline: book?.mallSettings?.tagline || '',
        centralizedCheckout: book?.mallSettings?.centralizedCheckout !== false,
        checkoutUrl: book?.mallSettings?.checkoutUrl || '',
        whatsapp: book?.mallSettings?.whatsapp || '',
        email: book?.mallSettings?.email || '',
        stores: Array.isArray(book?.mallSettings?.stores) ? book.mallSettings.stores : []
      },
    });
  };

  const handleInputChange = (path, value) => {
    console.log('Input change:', path, value); // Debug log
    
    setFormData(prev => {
      // Handle nested paths with dot notation
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        
        // Navigate to parent object
        let current = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          
          // Ensure nested object exists
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          
          current = current[key];
        }
        
        // Set the final value
        current[keys[keys.length - 1]] = value;
        
        return newData;
      }
      
      // Handle simple top-level properties
      return {
        ...prev,
        [path]: value
      };
    });
  };

  const handleNestedInputChange = (parentKey, childKey, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  const handleColumnsChange = (breakpoint, value) => {
    const numValue = parseInt(value);
    if (numValue >= 1 && numValue <= 8) {
      setFormData(prev => ({
        ...prev,
        columnsBreakpoints: {
          ...prev.columnsBreakpoints,
          [breakpoint]: numValue
        }
      }));
    }
  };

  const handleFileUpload = async (file, fileType) => {
    if (!file) return;

    setUploadStates(prev => ({ ...prev, [fileType]: { uploading: true, error: null } }));
    
    try {
      const result = await firestoreService.uploadFile(file, `pb-${book.id}/ecommerce/${fileType}`);
      
      setFormData(prev => ({
        ...prev,
        [fileType === 'logo' ? 'logoUrl' : 
        fileType === 'favicon' ? 'faviconUrl' : 
        fileType === 'headerBackground' ? 'headerBackgroundUrl' : 
        fileType]: result.url
      }));

      setUploadStates(prev => ({ ...prev, [fileType]: { uploading: false, error: null } }));
      
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      setUploadStates(prev => ({ 
        ...prev, 
        [fileType]: { uploading: false, error: error.message } 
      }));
    }
  };

  // File upload handlers for FileUpload component
  const handleLogoUpload = (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, logo: error }));
      return;
    }
    handleFileUpload(file, 'logo');
  };

  const handleFaviconUpload = (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, favicon: error }));
      return;
    }
    handleFileUpload(file, 'favicon');
  };

  const handleHeaderBgUpload = (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, headerBackground: error }));
      return;
    }
    handleFileUpload(file, 'headerBackground');
  };

  const handleOgImageUpload = (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, ogImage: error }));
      return;
    }
    handleFileUpload(file, 'ogImage');
  };

  const handleStoreTypeChange = (storeType) => {
    const storeConfig = STORE_TYPES[storeType];
    
    setFormData(prev => ({
      ...prev,
      storeType,
      enabledFeatures: {
        ...prev.enabledFeatures,
        ...storeConfig.defaultFeatures.reduce((acc, feature) => {
          acc[feature] = true;
          return acc;
        }, {})
      },
      design: {
        ...prev.design,
        primaryColor: storeConfig.color
      }
    }));
  };

  const addMallStore = () => {
    const storeId = newMallStore.storeId?.trim();
    const storeName = newMallStore.storeName?.trim();
    if (!storeId && !storeName) return;

    setFormData(prev => ({
      ...prev,
      mallSettings: {
        ...prev.mallSettings,
        stores: [
          ...(prev.mallSettings?.stores || []),
          {
            storeId: storeId || `store-${Date.now()}`,
            storeName: storeName || 'Storefront',
            description: newMallStore.description?.trim() || ''
          }
        ]
      }
    }));

    setNewMallStore({ storeId: '', storeName: '', description: '' });
  };

  const updateMallStore = (index, field, value) => {
    setFormData(prev => {
      const stores = [...(prev.mallSettings?.stores || [])];
      stores[index] = {
        ...stores[index],
        [field]: value
      };
      return {
        ...prev,
        mallSettings: {
          ...prev.mallSettings,
          stores
        }
      };
    });
  };

  const removeMallStore = (index) => {
    setFormData(prev => ({
      ...prev,
      mallSettings: {
        ...prev.mallSettings,
        stores: (prev.mallSettings?.stores || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Validation
      const errors = {};
      
      if (!formData.botName?.trim()) {
        errors.botName = 'Store name is required';
      }
      
      if (formData.customDomain && !isValidDomain(formData.customDomain)) {
        errors.customDomain = 'Please enter a valid domain (e.g., shop.yourdomain.com)';
      }
      
      if (formData.customPath && !/^[a-zA-Z0-9-]+$/.test(formData.customPath)) {
        errors.customPath = 'Path can only contain letters, numbers, and hyphens';
      }
      
      if (formData.seoTitle && formData.seoTitle.length > 60) {
        errors.seoTitle = 'SEO title should be 60 characters or less';
      }
      
      if (formData.seoDescription && formData.seoDescription.length > 160) {
        errors.seoDescription = 'SEO description should be 160 characters or less';
      }
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setLoading(false);
        return;
      }
      
      setValidationErrors({});

      // ✅ PROPER FIX: Deep merge nested objects instead of replacing them
      const updatedBook = {
        ...book,
        // Only spread top-level simple values that were actually changed
        botName: formData.botName,
        tagline: formData.tagline,
        description: formData.description,
        storeType: formData.storeType,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        website: formData.website,
        logoUrl: formData.logoUrl,
        faviconUrl: formData.faviconUrl,
        headerBackgroundUrl: formData.headerBackgroundUrl,
        ogImage: formData.ogImage,
        published: formData.published,
        accessLevel: formData.accessLevel,
        requireLogin: formData.requireLogin,
        allowIndexing: formData.allowIndexing,
        authorizedUsers: formData.authorizedUsers,
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
        businessType: formData.businessType,
        businessHours: formData.businessHours,
        
        // ✅ DEEP MERGE nested objects - preserve existing values
        headerSettings: {
          ...book.headerSettings,
          ...formData.headerSettings
        },
        
        layoutSettings: {
          ...book.layoutSettings,
          ...formData.layoutSettings
        },
        
        design: {
          ...book.design,
          ...formData.design
        },
        
        viewSettings: {
          ...book.viewSettings,
          ...formData.viewSettings
        },
        
        permissions: {
          ...book.permissions,
          ...formData.permissions
        },
        
        enabledFeatures: {
          ...book.enabledFeatures,
          ...formData.enabledFeatures
        },
        
        paymentSettings: {
          ...book.paymentSettings,
          ...formData.paymentSettings
        },
        
        shipping: {
          ...book.shipping,
          ...formData.shipping
        },
        
        tax: {
          ...book.tax,
          ...formData.tax
        },
        
        inventory: {
          ...book.inventory,
          ...formData.inventory
        },
        
        customer: {
          ...book.customer,
          ...formData.customer
        },
        
        advanced: {
          ...book.advanced,
          ...formData.advanced
        },

        mallSettings: {
          ...book.mallSettings,
          ...formData.mallSettings
        },
        
        updatedAt: new Date()
      };

      // Call onUpdate with the properly merged book
      await onUpdate(updatedBook);
      
      onClose();
      
    } catch (error) {
      console.error('Error saving ecommerce settings:', error);
      setValidationErrors({ 
        general: `Failed to save settings: ${error.message}. Please try again.` 
      });
    } finally {
      setLoading(false);
    }
  };

  const validatePaymentConfig = (paymentSettings) => {
    const errors = {};
    
    if (!paymentSettings) return errors;
    
    // Validate Stripe
    if (paymentSettings.paymentProcessor === 'stripe') {
      if (!paymentSettings.stripePublishableKey?.startsWith('pk_')) {
        errors['paymentSettings.stripePublishableKey'] = 'Valid Stripe Publishable Key required';
      }
      if (!paymentSettings.stripeSecretKey?.startsWith('sk_')) {
        errors['paymentSettings.stripeSecretKey'] = 'Valid Stripe Secret Key required';
      }
    }
    
    // Validate PayPal
    if (paymentSettings.paymentProcessor === 'paypal') {
      if (!paymentSettings.paypalClientId) {
        errors['paymentSettings.paypalClientId'] = 'PayPal Client ID is required';
      }
      if (!paymentSettings.paypalClientSecret) {
        errors['paymentSettings.paypalClientSecret'] = 'PayPal Client Secret is required';
      }
    }
    
    // Validate Square
    if (paymentSettings.paymentProcessor === 'square') {
      if (!paymentSettings.squareApplicationId) {
        errors['paymentSettings.squareApplicationId'] = 'Square Application ID is required';
      }
      if (!paymentSettings.squareAccessToken) {
        errors['paymentSettings.squareAccessToken'] = 'Square Access Token is required';
      }
    }
    
    // Validate PayFast
    if (paymentSettings.paymentProcessor === 'payfast') {
      if (!paymentSettings.payfastMerchantId) {
        errors['paymentSettings.payfastMerchantId'] = 'PayFast Merchant ID is required';
      }
      if (!paymentSettings.payfastMerchantKey) {
        errors['paymentSettings.payfastMerchantKey'] = 'PayFast Merchant Key is required';
      }
    }
    
    // Validate pricing
    if (paymentSettings.processingFeePercent && 
        (paymentSettings.processingFeePercent < 0 || paymentSettings.processingFeePercent > 100)) {
      errors['paymentSettings.processingFeePercent'] = 'Processing fee must be between 0 and 100';
    }
    
    if (paymentSettings.taxRate && 
        (paymentSettings.taxRate < 0 || paymentSettings.taxRate > 100)) {
      errors['paymentSettings.taxRate'] = 'Tax rate must be between 0 and 100';
    }
    
    return errors;
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Store },
    { id: 'features', label: 'Features', icon: Settings },
    { id: 'mall', label: 'Mall Admin', icon: Building2 },
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'layout', label: 'Layout', icon: Layout },
    { id: 'access', label: 'Access & Privacy', icon: Shield },
    { id: 'view', label: 'View Settings', icon: Globe },
    { id: 'permissions', label: 'Access Control', icon: Shield },
    { id: 'business', label: 'Business', icon: Users },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'shipping', label: 'Shipping', icon: Truck },
    { id: 'design', label: 'Design & Brand', icon: Palette },
    { id: 'marketing', label: 'Marketing', icon: BarChart3 },
    { id: 'seo', label: 'SEO & Sharing', icon: Globe }, 
    { id: 'advanced', label: 'Advanced', icon: Settings }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addAuthorizedUser = () => {
    setEmailError('');
    
    if (!newUserEmail.trim()) {
      setEmailError('Please enter an email address');
      return;
    }
    
    if (!validateEmail(newUserEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (formData.authorizedUsers?.includes(newUserEmail)) {
      setEmailError('This email is already authorized');
      return;
    }

    handleInputChange('authorizedUsers', [...(formData.authorizedUsers || []), newUserEmail.trim()]);
    setNewUserEmail('');
  };

  const removeAuthorizedUser = (email) => {
    const updatedUsers = formData.authorizedUsers.filter(user => user !== email);
    handleInputChange('authorizedUsers', updatedUsers);
  };

  const isValidDomain = (domain) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(domain);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Store className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Store Settings</h2>
              <p className="text-gray-600">Configure your eCommerce store</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setPreviewMode('desktop')}
                className={`p-2 ${previewMode === 'desktop' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600'}`}
              >
                <Monitor className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPreviewMode('tablet')}
                className={`p-2 ${previewMode === 'tablet' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600'}`}
              >
                <Tablet className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPreviewMode('mobile')}
                className={`p-2 ${previewMode === 'mobile' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600'}`}
              >
                <Smartphone className="h-4 w-4" />
              </button>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Settings Panel */}
          <div className="w-2/3 flex flex-col">
            {/* Tab Navigation */}
            <div className="flex border-b overflow-x-auto">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'basic' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Store Type</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(STORE_TYPES).map(([key, type]) => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={key}
                            onClick={() => handleStoreTypeChange(key)}
                            className={`p-4 border-2 rounded-lg text-left transition-all ${
                              formData.storeType === key
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <Icon className="h-6 w-6" style={{ color: type.color }} />
                              <span className="font-medium">{type.label}</span>
                            </div>
                            <p className="text-sm text-gray-600">{type.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Store Name</label>
                      <input
                        type="text"
                        value={formData.botName}
                        onChange={(e) => handleInputChange('botName', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Your Store Name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Tagline</label>
                      <input
                        type="text"
                        value={formData.tagline}
                        onChange={(e) => handleInputChange('tagline', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Your Store Tagline"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows="3"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe your store..."
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-6">
                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Store Logo
                      </label>
                      <FileUpload
                        onFileSelect={handleLogoUpload}
                        currentFile={formData.logoUrl}
                        onRemove={() => handleInputChange('logoUrl', '')}
                        accept="image/*"
                        maxSize={5}
                        label="Upload Logo"
                        uploading={uploadStates.logoUrl?.uploading || false}
                        error={uploadStates.logoUrl?.error || validationErrors.logo}
                        showPreview={true}
                      />
                      
                      {/* Logo Height Control */}
                      {formData.logoUrl && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Logo Height (pixels)
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="range"
                              min="20"
                              max="120"
                              value={formData.logoHeight || 40}
                              onChange={(e) => handleInputChange('logoHeight', parseInt(e.target.value))}
                              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="20"
                                max="120"
                                value={formData.logoHeight || 40}
                                onChange={(e) => handleInputChange('logoHeight', parseInt(e.target.value))}
                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-500">px</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Recommended: 40-60px for optimal appearance
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Replace Favicon Upload Section */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Favicon (16x16px recommended)
                      </label>
                      <FileUpload
                        onFileSelect={handleFaviconUpload}
                        currentFile={formData.favicon}
                        onRemove={() => handleInputChange('favicon', '')}
                        accept="image/*"
                        maxSize={1}
                        label="Upload Favicon"
                        uploading={uploadStates.favicon?.uploading || false}
                        error={uploadStates.favicon?.error || validationErrors.favicon}
                        showPreview={true}
                      />
                    </div>

                    {/* Header Background */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Header Background
                      </label>
                      <FileUpload
                        onFileSelect={handleHeaderBgUpload}
                        currentFile={formData.headerBackgroundUrl}
                        onRemove={() => handleInputChange('headerBackgroundUrl', '')}
                        accept="image/*"
                        maxSize={10}
                        label="Upload Background"
                        uploading={uploadStates.headerBackgroundUrl?.uploading || false}
                        error={uploadStates.headerBackgroundUrl?.error || validationErrors.headerBackground}
                        showPreview={true}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'mall' && (
                <div className="space-y-6">
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <h3 className="text-lg font-semibold text-blue-900">Digital Mall Admin</h3>
                    <p className="mt-1 text-sm text-blue-800">
                      Configure storefronts and centralized checkout settings for `storeType: mall`.
                    </p>
                    {formData.storeType !== 'mall' && (
                      <p className="mt-2 text-xs text-blue-700">
                        Current store type is <strong>{formData.storeType}</strong>. Switch to <strong>Digital Mall</strong> in Basic Info to use this in the public template.
                      </p>
                    )}
                  </div>

                  <div className="rounded-xl border border-gray-200 p-4">
                    <h4 className="mb-3 text-md font-semibold text-gray-900">Mall Details</h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Mall Name</label>
                        <input
                          type="text"
                          value={formData.mallSettings?.mallName || ''}
                          onChange={(e) => handleInputChange('mallSettings.mallName', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="Parrot Pages Digital Mall"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Tagline</label>
                        <input
                          type="text"
                          value={formData.mallSettings?.tagline || ''}
                          onChange={(e) => handleInputChange('mallSettings.tagline', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="Browse multiple stores and services in one place"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-4">
                    <h4 className="mb-3 text-md font-semibold text-gray-900">Centralized Checkout</h4>
                    <label className="mb-4 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.mallSettings?.centralizedCheckout !== false}
                        onChange={(e) => handleInputChange('mallSettings.centralizedCheckout', e.target.checked)}
                        className="rounded"
                      />
                      Enable centralized checkout
                    </label>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Checkout URL</label>
                        <input
                          type="url"
                          value={formData.mallSettings?.checkoutUrl || ''}
                          onChange={(e) => handleInputChange('mallSettings.checkoutUrl', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="https://checkout.example.com/mall"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">WhatsApp Number</label>
                        <input
                          type="text"
                          value={formData.mallSettings?.whatsapp || ''}
                          onChange={(e) => handleInputChange('mallSettings.whatsapp', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="27821234567"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Checkout Email</label>
                        <input
                          type="email"
                          value={formData.mallSettings?.email || ''}
                          onChange={(e) => handleInputChange('mallSettings.email', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="orders@yourmall.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-4">
                    <h4 className="mb-3 text-md font-semibold text-gray-900">Storefronts</h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <input
                        type="text"
                        value={newMallStore.storeId}
                        onChange={(e) => setNewMallStore(prev => ({ ...prev, storeId: e.target.value }))}
                        className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        placeholder="Store ID reference"
                      />
                      <input
                        type="text"
                        value={newMallStore.storeName}
                        onChange={(e) => setNewMallStore(prev => ({ ...prev, storeName: e.target.value }))}
                        className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        placeholder="Store name"
                      />
                      <button
                        type="button"
                        onClick={addMallStore}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4" />
                        Add Storefront
                      </button>
                    </div>
                    <textarea
                      value={newMallStore.description}
                      onChange={(e) => setNewMallStore(prev => ({ ...prev, description: e.target.value }))}
                      className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="Short storefront description (optional)"
                    />

                    <div className="mt-5 space-y-3">
                      {(formData.mallSettings?.stores || []).length === 0 ? (
                        <p className="rounded-lg border border-dashed border-gray-300 p-3 text-sm text-gray-500">
                          No storefronts added yet.
                        </p>
                      ) : (
                        formData.mallSettings.stores.map((store, index) => (
                          <div key={`${store.storeId || store.id || 'store'}-${index}`} className="rounded-lg border border-gray-200 p-3">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                              <input
                                type="text"
                                value={store.storeId || store.id || ''}
                                onChange={(e) => updateMallStore(index, 'storeId', e.target.value)}
                                className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                placeholder="Store ID"
                              />
                              <input
                                type="text"
                                value={store.storeName || store.name || ''}
                                onChange={(e) => updateMallStore(index, 'storeName', e.target.value)}
                                className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                placeholder="Store name"
                              />
                              <button
                                type="button"
                                onClick={() => removeMallStore(index)}
                                className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 hover:bg-red-100"
                              >
                                Remove
                              </button>
                            </div>
                            <textarea
                              value={store.description || ''}
                              onChange={(e) => updateMallStore(index, 'description', e.target.value)}
                              className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                              rows={2}
                              placeholder="Description (optional)"
                            />
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'header' && (
                <HeaderSettings
                  formData={formData}
                  onInputChange={handleInputChange}               
                  onImageUpload={handleFileUpload}
                />
              )}

              {activeTab === 'layout' && (
                <LayoutDisplaySection
                  formData={formData}
                  onInputChange={handleInputChange}
                  templateType="ecommerce"
                />
              )}

              {activeTab === 'features' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Store Features</h3>
                    <p className="text-gray-600 mb-6">Select the features you want to enable for your store</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(AVAILABLE_FEATURES).map(([key, feature]) => {
                        const Icon = feature.icon;
                        return (
                          <div
                            key={key}
                            className={`p-4 border-2 rounded-lg transition-all ${
                              formData.enabledFeatures[key]
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={formData.enabledFeatures[key] || false}
                                onChange={(e) => handleInputChange(key, e.target.checked, 'enabledFeatures')}
                                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Icon className="h-5 w-5 text-gray-600" />
                                  <span className="font-medium">{feature.label}</span>
                                </div>
                                <p className="text-sm text-gray-600">{feature.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'view' && (
                <ViewSettings
                  formData={formData}
                  onInputChange={handleInputChange}
                  templateType="ecommerce"
                />
              )}

              {activeTab === 'permissions' && (
                <PermissionsSettings 
                  bookId={bookId}
                  bookData={book}
                  formData={formData}
                  onInputChange={handleInputChange}
                  templateType="ecommerce"
                  currentUserRole="owner"
                />
              )}              

              {activeTab === 'business' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      Business Information
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Configure your business details, contact information, and operating hours.
                    </p>

                    {/* Business Details */}
                    <div className="bg-gray-50 p-6 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">Business Details</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Business Name *
                          </label>
                          <input
                            type="text"
                            value={formData.businessName || ''}
                            onChange={(e) => handleInputChange('businessName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Your Business Name"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Business Type
                          </label>
                          <select
                            value={formData.businessType || 'retail'}
                            onChange={(e) => handleInputChange('businessType', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="retail">Retail Store</option>
                            <option value="restaurant">Restaurant/Food Service</option>
                            <option value="services">Service Business</option>
                            <option value="manufacturing">Manufacturing</option>
                            <option value="wholesale">Wholesale</option>
                            <option value="nonprofit">Non-Profit</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Registration Number
                          </label>
                          <input
                            type="text"
                            value={formData.businessRegistration || ''}
                            onChange={(e) => handleInputChange('businessRegistration', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Company Registration Number"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            VAT Number
                          </label>
                          <input
                            type="text"
                            value={formData.vatNumber || ''}
                            onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="VAT Registration Number"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-blue-50 p-6 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-blue-900 mb-4 flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Contact Information
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Primary Email *
                          </label>
                          <input
                            type="email"
                            value={formData.email || ''}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="business@example.com"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Primary Phone *
                          </label>
                          <input
                            type="tel"
                            value={formData.phone || ''}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="+27 XX XXX XXXX"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            WhatsApp Number
                          </label>
                          <input
                            type="tel"
                            value={formData.whatsappNumber || ''}
                            onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="+27 XX XXX XXXX"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Customer Service Email
                          </label>
                          <input
                            type="email"
                            value={formData.customerServiceEmail || ''}
                            onChange={(e) => handleInputChange('customerServiceEmail', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="support@example.com"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business Address */}
                    <div className="bg-green-50 p-6 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-green-900 mb-4 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Business Address
                      </h4>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Street Address
                          </label>
                          <input
                            type="text"
                            value={formData.address || ''}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="123 Main Street"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              City
                            </label>
                            <input
                              type="text"
                              value={formData.city || ''}
                              onChange={(e) => handleInputChange('city', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Durban"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Province
                            </label>
                            <select
                              value={formData.province || ''}
                              onChange={(e) => handleInputChange('province', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select Province</option>
                              <option value="western-cape">Western Cape</option>
                              <option value="eastern-cape">Eastern Cape</option>
                              <option value="northern-cape">Northern Cape</option>
                              <option value="free-state">Free State</option>
                              <option value="kwazulu-natal">KwaZulu-Natal</option>
                              <option value="north-west">North West</option>
                              <option value="gauteng">Gauteng</option>
                              <option value="mpumalanga">Mpumalanga</option>
                              <option value="limpopo">Limpopo</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Postal Code
                            </label>
                            <input
                              type="text"
                              value={formData.postalCode || ''}
                              onChange={(e) => handleInputChange('postalCode', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="4001"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Business Hours */}
                    <div className="bg-purple-50 p-6 rounded-lg">
                      <h4 className="text-md font-semibold text-purple-900 mb-4 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Business Hours
                      </h4>
                      
                      <div className="space-y-3">
                        {Object.entries({
                          monday: 'Monday',
                          tuesday: 'Tuesday', 
                          wednesday: 'Wednesday',
                          thursday: 'Thursday',
                          friday: 'Friday',
                          saturday: 'Saturday',
                          sunday: 'Sunday'
                        }).map(([day, label]) => (
                          <div key={day} className="flex items-center gap-4">
                            <div className="w-24 text-sm font-medium text-gray-700">
                              {label}
                            </div>
                            <input
                              type="text"
                              value={formData.businessHours?.[day] || '9:00 AM - 5:00 PM'}
                              onChange={(e) => handleInputChange(day, e.target.value, 'businessHours')}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="9:00 AM - 5:00 PM or 'Closed'"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'access' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-600" />
                      Access & Privacy Settings
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Control who can access your store and manage privacy settings.
                    </p>

                    {/* Publishing Status - ADD THIS SECTION FIRST */}
                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-lg mb-6 border border-emerald-200">
                      <h4 className="text-md font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Publishing Status
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <label className="font-medium text-gray-900">Store Published</label>
                            <p className="text-sm text-gray-600">
                              {formData.published ? 
                                'Your store is live and accessible to customers' : 
                                'Your store is in draft mode and not publicly accessible'
                              }
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-medium ${
                              formData.published ? 'text-emerald-600' : 'text-orange-600'
                            }`}>
                              {formData.published ? 'Published' : 'Draft'}
                            </span>
                            <input
                              type="checkbox"
                              checked={formData.published || false}
                              onChange={(e) => handleInputChange('published', e.target.checked)}
                              className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                          </div>
                        </div>

                        {formData.published && (
                          <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-emerald-900">Store is Live</p>
                                <p className="text-xs text-emerald-700">
                                  Your store is accessible at: <code className="bg-white px-1 rounded">{formData.customDomain || `pb-books.web.app/store/${bookId}`}</code>
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {!formData.published && (
                          <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-orange-900">Store is in Draft Mode</p>
                                <p className="text-xs text-orange-700">
                                  Only you and authorized users can preview the store. Publish to make it live.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Publication Schedule */}
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <label className="font-medium text-gray-900">Schedule Publication</label>
                            <input
                              type="checkbox"
                              checked={formData.schedulePublication || false}
                              onChange={(e) => handleInputChange('schedulePublication', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </div>
                          <p className="text-sm text-gray-600 mb-3">Automatically publish your store at a specific date and time</p>
                          
                          {formData.schedulePublication && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Publication Date</label>
                                <input
                                  type="date"
                                  value={formData.publicationDate || ''}
                                  onChange={(e) => handleInputChange('publicationDate', e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Publication Time</label>
                                <input
                                  type="time"
                                  value={formData.publicationTime || ''}
                                  onChange={(e) => handleInputChange('publicationTime', e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Store Access Level */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-blue-900 mb-3">Store Access Level</h4>
                      <div className="space-y-3">
                        {[
                          { 
                            value: 'public', 
                            label: 'Public Store', 
                            description: 'Anyone can browse and purchase from your store',
                            icon: Globe
                          },
                          { 
                            value: 'private', 
                            label: 'Private Store', 
                            description: 'Only authorized users can access the store',
                            icon: Shield
                          },
                          { 
                            value: 'restricted', 
                            label: 'Restricted Access', 
                            description: 'Login required but anyone can create account',
                            icon: Users
                          },
                          { 
                            value: 'password', 
                            label: 'Password Protected', 
                            description: 'Single password required for store access',
                            icon: Lock
                          }
                        ].map((option) => {
                          const Icon = option.icon;
                          return (
                            <label key={option.value} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="radio"
                                name="accessLevel"
                                value={option.value}
                                checked={formData.accessLevel === option.value}
                                onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                              />
                              <Icon className="h-5 w-5 text-gray-600" />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{option.label}</div>
                                <div className="text-sm text-gray-600">{option.description}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Password Protection */}
                    {formData.accessLevel === 'password' && (
                      <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                        <h4 className="text-md font-semibold text-yellow-900 mb-3">Store Password</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Access Password
                            </label>
                            <input
                              type="password"
                              value={formData.accessPassword || ''}
                              onChange={(e) => handleInputChange('accessPassword', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter store access password"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Customers will need this password to access your store
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Authorized Users (for private stores) */}
                    {formData.accessLevel === 'private' && (
                      <div className="bg-purple-50 p-4 rounded-lg mb-6">
                        <h4 className="text-md font-semibold text-purple-900 mb-3">Authorized Users</h4>
                        <div className="space-y-3">
                          <div className="flex gap-2">
                            <input
                              type="email"
                              value={newUserEmail}
                              onChange={(e) => setNewUserEmail(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter email address"
                            />
                            <button
                              onClick={addAuthorizedUser}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              Add
                            </button>
                          </div>
                          
                          {emailError && (
                            <p className="text-sm text-red-600">{emailError}</p>
                          )}

                          {formData.authorizedUsers?.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-700">Authorized Users:</p>
                              {formData.authorizedUsers.map((email, index) => (
                                <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                                  <span className="text-sm text-gray-700">{email}</span>
                                  <button
                                    onClick={() => removeAuthorizedUser(email)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Customer Account Settings */}
                    <div className="bg-green-50 p-4 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-green-900 mb-3">Customer Account Settings</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="font-medium text-gray-900">Allow Guest Checkout</label>
                            <p className="text-sm text-gray-600">Customers can purchase without creating an account</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.allowGuestCheckout || false}
                            onChange={(e) => handleInputChange('allowGuestCheckout', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="font-medium text-gray-900">Require Account for Purchase</label>
                            <p className="text-sm text-gray-600">Force customers to create accounts before purchasing</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.requireAccountForPurchase || false}
                            onChange={(e) => handleInputChange('requireAccountForPurchase', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Age Restrictions */}
                    <div className="bg-orange-50 p-4 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-orange-900 mb-3">Age Restrictions</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="font-medium text-gray-900">Age Restriction Required</label>
                            <p className="text-sm text-gray-600">Verify customer age before allowing access</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.requireAge || false}
                            onChange={(e) => handleInputChange('requireAge', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>

                        {formData.requireAge && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Minimum Age
                            </label>
                            <input
                              type="number"
                              value={formData.ageLimit || 18}
                              onChange={(e) => handleInputChange('ageLimit', parseInt(e.target.value))}
                              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              min="13"
                              max="100"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Minimum age required to access the store
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Privacy & SEO Settings */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Privacy & SEO Settings</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="font-medium text-gray-900">Allow Search Engine Indexing</label>
                            <p className="text-sm text-gray-600">Let search engines find and index your store</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.allowIndexing !== false}
                            onChange={(e) => handleInputChange('allowIndexing', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="font-medium text-gray-900">Enable Right-Click Protection</label>
                            <p className="text-sm text-gray-600">Prevent right-click context menu on store pages</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.enableRightClickProtection || false}
                            onChange={(e) => handleInputChange('enableRightClickProtection', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="font-medium text-gray-900">Show Last Update Date</label>
                            <p className="text-sm text-gray-600">Display when the store was last updated</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.showLastUpdate !== false}
                            onChange={(e) => handleInputChange('showLastUpdate', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Terms & Conditions */}
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <h4 className="text-md font-semibold text-indigo-900 mb-3">Legal Requirements</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="font-medium text-gray-900">Require Terms Acceptance</label>
                            <p className="text-sm text-gray-600">Customers must accept terms before purchasing</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.requireTerms || false}
                            onChange={(e) => handleInputChange('requireTerms', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'payments' && (
                <PaymentsSection
                  formData={formData}
                  onInputChange={handleInputChange}
                  templateType="ecommerce"
                />
              )}

              {/*}

              {activeTab === 'payments' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-blue-600" />
                      Payment Configuration
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Configure payment methods and checkout options for your store.
                    </p>

                    {/* Online Payments 
                    <div className="bg-blue-50 p-6 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-blue-900 mb-4 flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Online Payment Gateway
                      </h4>
                      
                      {/* PayFast Integration 
                      <div className="bg-white p-4 rounded-lg border mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                              <CreditCard className="h-5 w-5 text-orange-600" />
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900">PayFast</h5>
                              <p className="text-sm text-gray-600">South African payment gateway</p>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.paymentMethods?.payfast?.enabled || false}
                            onChange={(e) => handleInputChange('payfast', { 
                              ...formData.paymentMethods?.payfast, 
                              enabled: e.target.checked 
                            }, 'paymentMethods')}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                        
                        {formData.paymentMethods?.payfast?.enabled && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Merchant ID
                                </label>
                                <input
                                  type="text"
                                  value={formData.paymentMethods?.payfast?.merchantId || ''}
                                  onChange={(e) => handleInputChange('payfast', {
                                    ...formData.paymentMethods?.payfast,
                                    merchantId: e.target.value
                                  }, 'paymentMethods')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="10000100"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Merchant Key
                                </label>
                                <input
                                  type="password"
                                  value={formData.paymentMethods?.payfast?.merchantKey || ''}
                                  onChange={(e) => handleInputChange('payfast', {
                                    ...formData.paymentMethods?.payfast,
                                    merchantKey: e.target.value
                                  }, 'paymentMethods')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="46f0cd694581a"
                                />
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.paymentMethods?.payfast?.sandbox || false}
                                onChange={(e) => handleInputChange('payfast', {
                                  ...formData.paymentMethods?.payfast,
                                  sandbox: e.target.checked
                                }, 'paymentMethods')}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label className="text-sm text-gray-700">Sandbox Mode (for testing)</label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Alternative Payment Methods 
                    <div className="bg-green-50 p-6 rounded-lg mb-6">
                      <h4 className="text-md font-semibold text-green-900 mb-4 flex items-center gap-2">
                        <Receipt className="h-4 w-4" />
                        Alternative Payment Methods
                      </h4>
                      
                      <div className="space-y-4">
                        {/* EFT Payment 
                        <div className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Receipt className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <h5 className="font-semibold text-gray-900">Electronic Funds Transfer (EFT)</h5>
                                <p className="text-sm text-gray-600">Bank transfer payments</p>
                              </div>
                            </div>
                            <input
                              type="checkbox"
                              checked={formData.paymentMethods?.eft?.enabled || false}
                              onChange={(e) => handleInputChange('eft', { 
                                ...formData.paymentMethods?.eft, 
                                enabled: e.target.checked 
                              }, 'paymentMethods')}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </div>
                          
                          {formData.paymentMethods?.eft?.enabled && (
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Bank Name
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.paymentMethods?.eft?.bankName || ''}
                                    onChange={(e) => handleInputChange('eft', {
                                      ...formData.paymentMethods?.eft,
                                      bankName: e.target.value
                                    }, 'paymentMethods')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="First National Bank"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Account Holder
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.paymentMethods?.eft?.accountHolder || ''}
                                    onChange={(e) => handleInputChange('eft', {
                                      ...formData.paymentMethods?.eft,
                                      accountHolder: e.target.value
                                    }, 'paymentMethods')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Business Name"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Account Number
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.paymentMethods?.eft?.accountNumber || ''}
                                    onChange={(e) => handleInputChange('eft', {
                                      ...formData.paymentMethods?.eft,
                                      accountNumber: e.target.value
                                    }, 'paymentMethods')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="1234567890"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Branch Code
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.paymentMethods?.eft?.branchCode || ''}
                                    onChange={(e) => handleInputChange('eft', {
                                      ...formData.paymentMethods?.eft,
                                      branchCode: e.target.value
                                    }, 'paymentMethods')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="250655"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Payment Instructions
                                </label>
                                <textarea
                                  value={formData.paymentMethods?.eft?.instructions || ''}
                                  onChange={(e) => handleInputChange('eft', {
                                    ...formData.paymentMethods?.eft,
                                    instructions: e.target.value
                                  }, 'paymentMethods')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  rows="2"
                                  placeholder="Please use your order number as reference and email proof of payment to..."
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Cash on Delivery 
                        <div className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Truck className="h-5 w-5 text-green-600" />
                              </div>
                              <div>
                                <h5 className="font-semibold text-gray-900">Pay on Delivery</h5>
                                <p className="text-sm text-gray-600">Cash or card payment on delivery</p>
                              </div>
                            </div>
                            <input
                              type="checkbox"
                              checked={formData.paymentMethods?.cod?.enabled || false}
                              onChange={(e) => handleInputChange('cod', { 
                                ...formData.paymentMethods?.cod, 
                                enabled: e.target.checked 
                              }, 'paymentMethods')}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </div>
                          
                          {formData.paymentMethods?.cod?.enabled && (
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Cash Payment
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={formData.paymentMethods?.cod?.acceptCash || false}
                                      onChange={(e) => handleInputChange('cod', {
                                        ...formData.paymentMethods?.cod,
                                        acceptCash: e.target.checked
                                      }, 'paymentMethods')}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-700">Accept cash payments</span>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Card Payment
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={formData.paymentMethods?.cod?.acceptCard || false}
                                      onChange={(e) => handleInputChange('cod', {
                                        ...formData.paymentMethods?.cod,
                                        acceptCard: e.target.checked
                                      }, 'paymentMethods')}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-700">Accept card payments</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  COD Fee
                                </label>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-500">R</span>
                                  <input
                                    type="number"
                                    value={formData.paymentMethods?.cod?.fee || 0}
                                    onChange={(e) => handleInputChange('cod', {
                                      ...formData.paymentMethods?.cod,
                                      fee: parseFloat(e.target.value) || 0
                                    }, 'paymentMethods')}
                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    min="0"
                                    step="0.01"
                                  />
                                  <span className="text-sm text-gray-500">additional fee for COD</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Payment Security 
                    <div className="bg-yellow-50 p-6 rounded-lg">
                      <h4 className="text-md font-semibold text-yellow-900 mb-4 flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Payment Security & Settings
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <label className="font-medium text-gray-900">Require Terms Acceptance</label>
                            <p className="text-sm text-gray-600">Customers must accept terms before payment</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.paymentSettings?.requireTerms || false}
                            onChange={(e) => handleInputChange('requireTerms', e.target.checked, 'paymentSettings')}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <label className="font-medium text-gray-900">Email Payment Confirmations</label>
                            <p className="text-sm text-gray-600">Send automatic payment confirmations</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.paymentSettings?.emailConfirmations || true}
                            onChange={(e) => handleInputChange('emailConfirmations', e.target.checked, 'paymentSettings')}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <label className="font-medium text-gray-900">Save Payment Methods</label>
                            <p className="text-sm text-gray-600">Allow customers to save payment details</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.paymentSettings?.savePaymentMethods || false}
                            onChange={(e) => handleInputChange('savePaymentMethods', e.target.checked, 'paymentSettings')}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              */}
              {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-600" />
                    SEO & Social Media
                  </h3>
                  
                  {/* Custom Domain & Path Configuration - MOVED TO ROOT LEVEL */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg mb-6">
                    <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Link className="h-4 w-4" />
                      Custom Domain & Path Configuration
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Domain
                        </label>
                        <input
                          type="text"
                          value={formData.customDomain || ''}
                          onChange={(e) => handleInputChange('customDomain', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            validationErrors.customDomain 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                          placeholder="yourdomain.com"
                        />
                        {validationErrors.customDomain && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors.customDomain}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Your custom domain (without https://)
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Path
                        </label>
                        <input
                          type="text"
                          value={formData.customPath || ''}
                          onChange={(e) => handleInputChange('customPath', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="store"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Custom path for your store (optional)
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-100 p-3 rounded border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Your store will be available at:</strong><br />
                        {formData.customDomain 
                          ? `https://${formData.customDomain}${formData.customPath ? `/${formData.customPath}` : ''}`
                          : `https://parrotpages.com/${bookId}${formData.customPath ? `/${formData.customPath}` : ''}`
                        }
                      </p>
                    </div>
                  </div>

                  {/* SEO Settings - customDomain/customPath REMOVED from here */}
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold text-gray-900">SEO Settings</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SEO Title
                        </label>
                        <input
                          type="text"
                          value={formData.seoTitle || ''}
                          onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={`${formData.botName} - Online Store`}
                        />
                        <p className="text-xs text-gray-500 mt-1">Recommended: 50-60 characters</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SEO Keywords
                        </label>
                        <input
                          type="text"
                          value={formData.seoKeywords || ''}
                          onChange={(e) => handleInputChange('seoKeywords', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="ecommerce, store, products"
                        />
                        <p className="text-xs text-gray-500 mt-1">Separate keywords with commas</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Description
                      </label>
                      <textarea
                        value={formData.seoDescription || ''}
                        onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Describe your store for search engines..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Recommended: 150-160 characters</p>
                    </div>

                    {/* Favicon Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Favicon</label>
                      <FileUpload
                        onFileSelect={handleFaviconUpload}
                        currentFile={formData.faviconUrl}
                        onRemove={() => handleInputChange('faviconUrl', '')}
                        accept="image/*"
                        maxSize={1}
                        label="Upload Favicon"
                        uploading={uploadStates.favicon?.uploading || false}
                        error={uploadStates.favicon?.error || validationErrors.favicon}
                        showPreview={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        32x32px PNG or ICO recommended
                      </p>
                    </div>

                    {/* OG Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Social Media Image (OG Image)</label>
                      <FileUpload
                        onFileSelect={handleOgImageUpload}
                        currentFile={formData.ogImage}
                        onRemove={() => handleInputChange('ogImage', '')}
                        accept="image/*"
                        maxSize={5}
                        label="Upload OG Image"
                        uploading={uploadStates.ogImage?.uploading || false}
                        error={uploadStates.ogImage?.error || validationErrors.ogImage}
                        showPreview={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        1200x630px recommended for social sharing
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

              {activeTab === 'design' && (
              <DesignBrandingSection
                formData={{
                  primaryColor: formData.design?.primaryColor,
                  backgroundColor: formData.design?.backgroundColor,
                  headerColor: formData.design?.headerColor,
                  headerTextColor: formData.design?.headerTextColor,
                  accentColor: formData.design?.accentColor,
                  fontFamily: formData.design?.fontFamily,
                  cardRadius: formData.design?.cardRadius,
                  shadowLevel: formData.design?.shadowLevel,
                  logoUrl: formData.logoUrl,
                  faviconUrl: formData.faviconUrl,
                  headerBackgroundUrl: formData.headerBackgroundUrl
                }}
                onInputChange={(field, value) => {
                  // Route design fields to design object
                  if (['primaryColor', 'backgroundColor', 'headerColor', 'headerTextColor', 
                      'accentColor', 'fontFamily', 'cardRadius', 'shadowLevel'].includes(field)) {
                    handleInputChange(`design.${field}`, value);
                  } else {
                    // Logo, favicon, etc. stay at root level
                    handleInputChange(field, value);
                  }
                }}
                onImageUpload={handleFileUpload}
                uploadStates={uploadStates}
                errors={validationErrors}
              />
            )}

            {activeTab === 'shipping' && (
              <ShippingSection
                formData={formData}
                onInputChange={handleInputChange}
                bookId={bookId}
              />
            )}

              {activeTab === 'marketing' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Marketing & Analytics</h3>
                    
                    {/* Social Media Links */}
                    <div className="space-y-4 mb-6">
                      <h4 className="text-md font-semibold text-gray-900">Social Media</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Instagram className="h-4 w-4 inline mr-1" />
                            Instagram
                          </label>
                          <input
                            type="url"
                            value={formData.socialMedia?.instagram || ''}
                            onChange={(e) => handleInputChange('instagram', e.target.value, 'socialMedia')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="https://instagram.com/yourstore"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Facebook className="h-4 w-4 inline mr-1" />
                            Facebook
                          </label>
                          <input
                            type="url"
                            value={formData.socialMedia?.facebook || ''}
                            onChange={(e) => handleInputChange('facebook', e.target.value, 'socialMedia')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="https://facebook.com/yourstore"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Analytics Integration */}
                    <div className="space-y-4">
                      <h4 className="text-md font-semibold text-gray-900">Analytics</h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Google Analytics ID
                        </label>
                        <input
                          type="text"
                          value={formData.integrations?.googleAnalytics || ''}
                          onChange={(e) => handleInputChange('googleAnalytics', e.target.value, 'integrations')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="G-XXXXXXXXXX"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Facebook Pixel ID
                        </label>
                        <input
                          type="text"
                          value={formData.integrations?.facebookPixel || ''}
                          onChange={(e) => handleInputChange('facebookPixel', e.target.value, 'integrations')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="123456789"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Preview Panel */}
          <div className="w-1/3 border-l bg-gray-50 p-6">
            <div className="mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Preview</h4>
              <p className="text-sm text-gray-600">See how your store will look</p>
            </div>

            <div className={`bg-white rounded-lg shadow-lg overflow-hidden mx-auto ${
              previewMode === 'mobile' ? 'w-64 h-96' : 
              previewMode === 'tablet' ? 'w-80 h-96' : 
              'w-full h-96'
            }`}>
              {formData.storeType === 'mall' ? (
                <div className="h-full overflow-y-auto">
                  <DigitalMallTemplate
                    isPreview={true}
                    bookData={{
                      botName: formData.botName,
                      title: formData.botName,
                      design: formData.design,
                      mallSettings: formData.mallSettings
                    }}
                    storeSettings={{ mallSettings: formData.mallSettings }}
                    styling={{
                      primaryColor: formData.design?.primaryColor,
                      accentColor: formData.design?.accentColor,
                      backgroundColor: formData.design?.backgroundColor,
                      fontFamily: formData.design?.fontFamily
                    }}
                  />
                </div>
              ) : (
                <>
                  {/* Store Preview */}
                  <div 
                    className="h-16 border-b flex items-center px-4"
                    style={{ 
                      backgroundColor: formData.design.headerBackgroundColor,
                      color: formData.design.headerTextColor 
                    }}
                  >
                    {formData.logoUrl && (
                      <img src={formData.logoUrl} alt="Logo" className="h-8 mr-3" />
                    )}
                    <div>
                      <div className="font-semibold text-sm">
                        {formData.botName || 'Store Name'}
                      </div>
                      {formData.tagline && (
                        <div className="text-xs opacity-75">{formData.tagline}</div>
                      )}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="text-xs text-gray-600 mb-3">Featured Products</div>
                    <div className="grid grid-cols-2 gap-2">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="border rounded-lg p-2">
                          <div className="h-16 bg-gray-200 rounded mb-2"></div>
                          <div className="text-xs font-medium">Product {i}</div>
                          <div 
                            className="text-xs font-bold"
                            style={{ color: formData.design.primaryColor }}
                          >
                            $99.99
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Changes will be saved automatically
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
